/**
 * Ad event-type funnel context.
 *
 * Coffee landing pages send users into the quiz with `?et=c`, dinner landings
 * with `?et=d`. Two consumers:
 *
 * 1. Quiz copy nudge — when `?et=c` is in the URL, a handful of the most
 *    dinner-specific quiz strings swap to coffee wording via the i18next
 *    `_coffee` context; without it, everything reads the default (dinner) keys.
 * 2. Guided post-signup booking (experiment) — the `et` value is persisted to
 *    localStorage on first load so that, after the quiz + signup complete, the
 *    guided booking screen can steer the user to the next event of the type
 *    they were acquired for. Persistence is passive: nothing outside the
 *    flag-gated guided flow reads the stored value, so control behavior is
 *    unchanged.
 */

const AD_EVENT_TYPE_KEY = "outclose_ad_event_type";

export type AdEventType = "coffee" | "dinner";

function parseEtParam(et: string | null): AdEventType | undefined {
  if (et === "c") return "coffee";
  if (et === "d") return "dinner";
  return undefined;
}

export function getQuizEventTypeContext(): "coffee" | undefined {
  if (typeof window === "undefined") return undefined;
  const et = new URLSearchParams(window.location.search).get("et");
  return et === "c" ? "coffee" : undefined;
}

/**
 * Persist the `?et=` ad event type for the guided post-signup booking flow.
 * Called once on app load (TrackingInitializer), mirroring how first-touch
 * attribution is captured. Last touch wins so a user who clicks a dinner ad
 * after a coffee ad gets dinner guidance.
 */
export function persistAdEventTypeFromURL(): void {
  if (typeof window === "undefined") return;
  const type = parseEtParam(new URLSearchParams(window.location.search).get("et"));
  if (!type) return;
  try {
    localStorage.setItem(AD_EVENT_TYPE_KEY, type);
  } catch {
    /* best-effort — private mode etc. */
  }
}

export function getStoredAdEventType(): AdEventType | null {
  if (typeof window === "undefined") return null;
  try {
    const stored = localStorage.getItem(AD_EVENT_TYPE_KEY);
    return stored === "coffee" || stored === "dinner" ? stored : null;
  } catch {
    return null;
  }
}

export function clearStoredAdEventType(): void {
  try {
    localStorage.removeItem(AD_EVENT_TYPE_KEY);
  } catch {
    /* best-effort */
  }
}
